import { addDays, format, isValid, isWeekend, parseISO } from "date-fns";

export interface ComputeNextScheduleDateArgs {
  /** Date of the most recently scheduled entry, if any (YYYY-MM-DD or ISO). */
  lastEntryDate?: string;
  /** Plan start date, used when there are no existing entries. */
  planStartDate?: string;
  /** Override for "today" — primarily for testability. */
  now?: Date;
}

// YYYY-MM-DD is parsed by `new Date()` / `parseISO` as UTC midnight, which can
// shift the day in negative-offset timezones once we format back to a local
// calendar date. Build a local-zone Date for the calendar-only form so that
// addDays/isWeekend/format all agree on the intended day.
function parseCalendarDate(input: string): Date | undefined {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input);
  if (dateOnly) {
    const [, y, m, d] = dateOnly;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return isValid(date) ? date : undefined;
  }
  const parsed = parseISO(input);
  return isValid(parsed) ? parsed : undefined;
}

/**
 * Picks the next business day to schedule:
 *   - last entry's date + 1 day, else plan start date, else today;
 *   - then advances forward past Sat/Sun.
 *
 * Returns a YYYY-MM-DD string.
 */
export function computeNextScheduleDate({
  lastEntryDate,
  planStartDate,
  now,
}: ComputeNextScheduleDateArgs): string {
  const lastParsed = lastEntryDate ? parseCalendarDate(lastEntryDate) : undefined;
  const planParsed = planStartDate ? parseCalendarDate(planStartDate) : undefined;

  let next: Date;
  if (lastParsed) {
    next = addDays(lastParsed, 1);
  } else if (planParsed) {
    next = planParsed;
  } else {
    next = now ?? new Date();
  }
  while (isWeekend(next)) {
    next = addDays(next, 1);
  }
  return format(next, "yyyy-MM-dd");
}
